import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRFQDeclines } from '@/hooks/useRFQDeclines';
import { useVendorNavCounts } from '@/contexts/VendorNavCountsContext';
import { DECLINE_REASONS } from '@/lib/types/job';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface DeclineToBidModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rfqId: string;
}

export const DeclineToBidModal = ({ open, onOpenChange, rfqId }: DeclineToBidModalProps) => {
  const navigate = useNavigate();
  const { declineRFQ, loading } = useRFQDeclines();
  const { refreshCounts } = useVendorNavCounts();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [notes, setNotes] = useState('');

  const handleConfirmDecline = async () => {
    if (!selectedReason) {
      toast.error('Please select a reason for declining');
      return;
    }

    try {
      await declineRFQ(rfqId, selectedReason, notes || undefined);
      toast.success('RFQ declined. Redirecting to Browse RFQs...');
      onOpenChange(false);
      
      // Refresh nav counts immediately
      refreshCounts();
      
      // Redirect after a short delay to show the toast
      setTimeout(() => {
        navigate('/rfqs');
      }, 1000);
    } catch (error) {
      console.error('Error declining RFQ:', error);
      toast.error('Failed to decline RFQ. Please try again.');
    }
  };

  const handleCancel = () => {
    setSelectedReason('');
    setNotes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Decline to Bid</DialogTitle>
          <DialogDescription>
            Please select a reason for declining this RFQ. This information helps clients improve future opportunities.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This RFQ will be hidden from your Browse RFQs page after you confirm.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <Label>Reason for declining *</Label>
            <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
              {DECLINE_REASONS.map((reason) => (
                <div key={reason} className="flex items-center space-x-2">
                  <RadioGroupItem value={reason} id={reason} />
                  <Label htmlFor={reason} className="font-normal cursor-pointer">
                    {reason}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Additional notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional context..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={4}
            />
            <p className="text-sm text-muted-foreground">
              {notes.length}/500 characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirmDecline}
            disabled={loading || !selectedReason}
          >
            {loading ? 'Declining...' : 'Confirm Decline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
